import { Component } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CollectedPreviewItem, DeckCard, DeckCardChanges, ItemSearch, LayoutType, SearchRequest, SearchResponse, UpdateDeckRequest, UpdateDeckResponse } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { take } from "rxjs";
import { environment } from "../../../../../../environments/environment";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { formatDate, shortenedName } from "@ponder-shared/utils/general";
import { formatCurrency } from "@ponder-shared/utils/numberFormatter";
import { NgClass } from "@angular/common";
import { DeckBaseComponent } from "../deck-base.component";

export enum AlternateCardModalAction {
    SwitchVersion = 0,
    AddVersion = 1
}

@Component({
    imports: [LoadingComponent, NgClass],
    templateUrl: "./deck-alternate-card-modal.component.html"
})
export class DeckAlternateCardModalComponent {

    public deckComponent!: DeckBaseComponent;
    public deckCard?: DeckCard;
    public alternateVersions?: CollectedPreviewItem[];

    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;
    
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly collectedSvgIcon: string = SvgIconMap.get(SvgIcon.CheckCircle)!;

    private setFilter?: string;
    private collectedOnly: boolean = false;
    private action: AlternateCardModalAction = AlternateCardModalAction.SwitchVersion;

    public constructor(private ponderDataClient: PonderDataClient, private events: EventBroker) {
        this.startSearch();
    }

    public reset(deckCard: DeckCard | undefined, action: AlternateCardModalAction) {
        this.deckCard = deckCard;
        this.isLoading = false;
        this.loadingSuccess = false;
        this.setFilter = undefined;
        this.collectedOnly = false;
        this.action = action;

        this.startSearch();
    }

    private startSearch(): void {
        var cardName = this.getCardName();

        if (!cardName) {
            return;
        }

        this.isLoading = true;

        var searchRequest = new SearchRequest({
            game: this.deckCard!.card.item.game!,
            item: new ItemSearch({
                name: cardName,
                exact: true,
                distinct: false,
                useSetPriority: false,
                sortCollectedCount: false
            })
        });

        this.ponderDataClient
            .search(searchRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: SearchResponse) => {
                    if (response.success && response.results) {
                        this.loadingSuccess = true;
                        this.alternateVersions = response.results;
                    }
                }
            })
            .add(() => {
                this.isLoading = false;
            });
    }

    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public onCloseClicked(): void {
        this.reset(undefined, this.action);
    }

    public onVersionSelected(card: CollectedPreviewItem): void {
        if (card.item.id! !== this.deckCard!.card.item.id!) {
            if (this.action == AlternateCardModalAction.SwitchVersion) {
                this.onVersionSelectedSwitchVersion(card);
            } else {
                this.onVersionSelectedAddVersion(card);
            }
        } 
        
        this.onCloseClicked();
    }

    private onVersionSelectedSwitchVersion(card: CollectedPreviewItem): void {
        if (!this.deckCard) {
            return;
        }
        
        var updateRequest = new UpdateDeckRequest({
            deckId: this.deckComponent.deck.id,
            cardChanges: [
                new DeckCardChanges({
                    cardId: this.deckCard!.deckCardId,
                    altVersion: card.item.id!
                })
            ]
        });

        var prevCard = this.deckCard.card;
        this.deckCard.card = card;
        
        this.events.deck.onCardVersionSwitchedEvent.emit(this.deckCard);

        this.ponderDataClient
            .deckUpdate(updateRequest)
            .subscribe({
                next: (response: UpdateDeckResponse) => {
                    if (!response.success) {
                        this.deckCard!.card = prevCard;
                        this.events.deck.onCardVersionSwitchedEvent.emit(this.deckCard);
                    }
                },
                error: () => {
                    this.deckCard!.card = prevCard;
                    this.events.deck.onCardVersionSwitchedEvent.emit(this.deckCard);
                }
            })
            .add(() => { this.reset(undefined, this.action); });
    }

    private onVersionSelectedAddVersion(card: CollectedPreviewItem): void {
        var specialDelta = card.collected?.specialCount ? 1 : 0;      // Add foil version if we have one
        var normalDelta = (specialDelta > 0 ? 0 : 1);

        this.deckComponent.addCardToActiveLayer(card.item.name!, card.item.id!, false, normalDelta, specialDelta);
    }

    public onCheckboxToggle(checkbox: HTMLInputElement): void {
        this.collectedOnly = checkbox.checked;
    }

    public onFilterUpdated(input: HTMLInputElement): void {
        this.setFilter = input.value.toLowerCase();
    }

    // -------------------------------------------------------------------------------------
    // Data
    // -------------------------------------------------------------------------------------

    private getCardName(): string | undefined {
        if (!this.deckCard) {
            return undefined;
        }

        if (this.deckCard.card.item.data?.magic?.faces) {
            return this.deckCard.card.item.data.magic.faces[0].name;
        } else {
            return undefined;
        }
    }

    public getCardImageUrl(card: CollectedPreviewItem): string {
        return `${environment.ponderImageBaseUrl}/card/small/front/${card.item!.id!}.jpg`;
    }

    public getShortenedName(card: CollectedPreviewItem): string {
        if (card.item.data?.magic) {
            return shortenedName(card.item.data.magic.set.name!, 19);
        } else {
            return "N/A";
        }
    }

    public getTooltipName(card: CollectedPreviewItem): string {
        if (card.item.data?.magic) {
            return card.item.data.magic.set.name!;
        } else {
            return "N/A";
        }
    }

    public getSetIndex(card: CollectedPreviewItem): string {
        if (card.item.data?.magic) {
            return `${card.item.data.magic.set.code!} (${card.item.data.magic.setIndex})`.toUpperCase();
        } else {
            return "N/A"
        }
    }

    public getReleaseDate(card: CollectedPreviewItem): string {
        if (card.item.data?.magic) {
            return formatDate(card.item.data.magic.releaseDate!)
        } else {
            return "N/A"
        }
    }

    public getCardValue(card: CollectedPreviewItem): string {
        if (card.price?.normalUSD) {
            return formatCurrency(card.price.normalUSD);
        } else {
            return "";
        }
    }

    public getCardSpecialValue(card: CollectedPreviewItem): string {
        if (card.price?.specialUSD) {
            return formatCurrency(card.price.specialUSD);
        } else {
            return "";
        }
    }

    public getCollectedCount(card: CollectedPreviewItem): number {
        return card.collected?.count ?? 0;
    }

    public getCollectedSpecialCount(card: CollectedPreviewItem): number {
        return card.collected?.specialCount ?? 0;
    }

    public isCollected(card: CollectedPreviewItem): boolean {
        return (card.collected?.count ? true : false) || (card.collected?.specialCount ? true : false);
    }

    public getProxyCount(card: CollectedPreviewItem): number {
        return card.collected?.proxyCount ?? 0;
    }

    public shouldShow(card: CollectedPreviewItem): boolean {
        if (this.action == AlternateCardModalAction.AddVersion && this.deckCard?.card.item.id == card.item.id) {
            return false;
        }

        if (this.collectedOnly) {
            if (!card.collected?.count && !card.collected?.specialCount && !card.collected?.proxyCount) {
                return false;
            }
        }

        if (this.setFilter) {
            if (card.item.data?.magic?.set) {
                if (card.item.data.magic.set.name!.toLowerCase().indexOf(this.setFilter) < 0 &&
                    card.item.data.magic.set.code!.toLowerCase().indexOf(this.setFilter) < 0) {
                    return false;
                }
            }
        }

        if (card.item.data?.magic?.layout == LayoutType.ArtSeries) {
            // Hide art cards
            return false;
        }

        return true;
    }

    public hasAlternateVersionsToShow(): boolean {
        if (this.alternateVersions!.length == 1) {
            return false;
        } else {
            return true;
        }
    }
}